"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "How does revenue management improve hotel profitability?",
  answer: "Revenue management optimizes your pricing and inventory in real-time based on market demand, competitor activity, and historical data. By ensuring the right room is sold to the right guest at the right price, we maximize your RevPAR and overall profitability."
}, {
  question: "What is the benefit of OTA optimization?",
  answer: "OTA optimization increases your visibility on platforms like Booking.com and Expedia. By refining your listings, managing content, and leveraging data-driven strategies, we improve your ranking, conversion rates, and direct booking potential."
}, {
  question: "Do you provide support for hotel technology stacks?",
  answer: "Yes. We specialize in recommending and implementing hospitality tech stacks, including Channel Managers, PMS, and Booking Engines, ensuring seamless integration that streamlines your operations and enhances the guest experience."
}, {
  question: "What is included in a revenue audit?",
  answer: "Our comprehensive revenue audit evaluates your current pricing strategies, distribution channels, and tech utilization. We identify missed opportunities and provide actionable insights to boost your hotel's revenue performance."
}, {
  question: "How long does it take to see results?",
  answer: "While some optimizations yield immediate improvements in visibility, significant revenue growth is typically observed within 30 to 90 days as we refine pricing strategies and stabilize your distribution performance."
}, {
  question: "What makes HotelRevBaBa different?",
  answer: "We combine deep technical expertise with a hands-on, personalized approach. Led by Sunil Singh, we treat every property as a unique asset, focusing on data-driven strategies that deliver measurable, long-term financial success."
}, {
  question: "Can you help with direct booking growth?",
  answer: "Absolutely. We implement targeted digital marketing and website optimization strategies designed to shift bookings from high-commission OTAs to your own direct booking engine, significantly reducing your acquisition costs."
}, {
  question: "How do I get started with a consultation?",
  answer: "Getting started is simple. Click the 'Book a Consultation' button to schedule a discovery call. We will discuss your property's specific challenges and outline a tailored strategy to maximize your revenue potential."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="casestudyfaq" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-xs tracking-widest uppercase text-blue-700 mb-8">
                REVENUE INSIGHTS / 03
              </div>

              <h2 className="font-default font-bold text-slate-900 mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Expert
                <br />
                Revenue
                <br />
                Answers.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "rgb(71 85 105)"
            }}>
                We provide clarity on complex hospitality challenges. If your specific question isn't addressed below, our team is ready to discuss your property's needs.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-blue-900 text-blue-900 bg-transparent hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300 px-8 py-6 h-auto">
                Book a Consultation
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group" style={{
              backgroundColor: "white",
              border: "1px solid rgb(226 232 240)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:rgb(203 213 225)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-blue-600 shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-slate-900 leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-blue-600 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "rgb(71 85 105)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}